import { useEffect, useState } from "react";
import { useAtom } from "jotai";
import { PlatformProvider } from "@NBCUDTC/reactjs-utility-belt/components/contexts/PlatformContext";
import "@/assets/iconFont/streaming-icons.css";
import { logger } from "@NBCUDTC/reactjs-utility-belt/logger";

import { Mobile } from "@/components/ecosystems/Mobile";
import { LoginPage } from "@/components/pages/LoginPage";
import { InstallModal } from "@/components/molecules/InstallModal";
import { platformAtom, currentUserAtom } from "@/constants/atoms";
import { useClassNames } from "@/utils/classNames";
import { Platform } from "@/types";
import { PageData } from "@/utils/FetchPageData/types";
import { fetchAirtableRecord } from "@/utils/airtable/FetchAirtableRecord";
import { fetchPageData } from "@/utils/FetchPageData";
import { AirtableTableId } from "@/utils/airtable/types";
import { Tile34 } from "@/components/molecules/Tile34";
import { TileMetadataBlock } from "@/components/molecules/TileMetadataBlock";
import { Tile169Size } from "@/components/molecules/Tile169/types";
import { Rail } from "@/components/organisms/Rail";
import { StreamingIcons } from "@/assets/iconFont/streaming-icons";
import { fetchAssetDetails } from "@/utils/FetchAssetDetails";
import { isSeries } from "@/utils/FetchAssetDetails/types";
import { fetchSeasonDetails } from "@/utils/FetchSeasonDetails";
import { fetchEpisodeDetails } from "@/utils/FetchEpisodeDetails";
import { getTileFormat } from "@/utils/GetTileFormat";
import { useFirebaseAuth } from "@/utils/hooks/useFirebaseAuth";

import styles from "./App.module.scss";

function App() {
  const [page, setPage] = useState<PageData>();
  const [platform, setPlatform] = useAtom(platformAtom);
  const [currentUser] = useAtom(currentUserAtom);

  // Initialize Firebase auth listener
  useFirebaseAuth();

  useEffect(() => {
    const envPlatform = process.env.PLATFORM || Platform.TV;
    const platformMap: Record<string, Platform> = {
      tv: Platform.TV,
      web: Platform.Web,
      mobile: Platform.Mobile,
    };
    setPlatform(platformMap[envPlatform] || Platform.TV);
  }, [setPlatform]);

  // Fetch Page Data
  useEffect(() => {
    const fetchAirtable = async () => {
      const pageId: string = "recdTLyT0Ljl22PDQ";
      const pageData = await fetchAirtableRecord(
        `${AirtableTableId.Pages}/${pageId}`,
      );
      const page = await fetchPageData(pageData);
      setPage(page);
    };

    fetchAirtable();
  }, []);

  const cn = useClassNames(styles, styles["app"]);

  if (!currentUser) {
    return (
      <>
        <InstallModal />
        <LoginPage />
      </>
    );
  }

  return (
    <PlatformProvider value={platform}>
      <InstallModal />
      <div className={cn}>
        {platform === Platform.Mobile ? (
          <Mobile />
        ) : (
          <div className={styles["app-content"]}>
            {page?.rails.map((rail) => (
              <Rail
                key={rail.self.id}
                title={rail.self.fields.Name}
                icon={StreamingIcons.Lock}
              >
                {rail.assets &&
                  rail.assets.map((asset) => (
                    <Tile34
                      key={asset.id}
                      format={getTileFormat(asset.atom)}
                      asset={asset.atom}
                      locked={false}
                    />
                  ))}
              </Rail>
            ))}

            {page?.rails[0].assets && (
              <div style={{ background: "gray" }}>
                <TileMetadataBlock
                  asset={page.rails[0].assets[0].atom}
                  tileSize={Tile169Size.Medium}
                  progress={25}
                />
                <TileMetadataBlock
                  asset={page.rails[0].assets[1].atom}
                  tileSize={Tile169Size.Large}
                  progress={50}
                />
                <TileMetadataBlock
                  asset={page.rails[0].assets[2].atom}
                  tileSize={Tile169Size.XLarge}
                  progress={75}
                />
                <TileMetadataBlock
                  asset={page.rails[0].assets[3].atom}
                  tileSize={Tile169Size.XLarge}
                />
                <TileMetadataBlock
                  asset={page.rails[0].assets[4].atom}
                  tileSize={Tile169Size.XLarge}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </PlatformProvider>
  );
}

export { App };
